/**
 * Rate Limit Context
 * Provides global rate limit error handling and upgrade modal management
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { RateLimitError, UserTier } from '../types/tier'
import { RATE_LIMIT_EVENT, RateLimitEventDetail } from '../utils/api'
import { UpgradeModal } from '../components/UpgradeModal'

interface RateLimitContextValue {
  showUpgradeModal: (error?: RateLimitError) => void
  hideUpgradeModal: () => void
  currentTier: UserTier
  setCurrentTier: (tier: UserTier) => void
}

const RateLimitContext = createContext<RateLimitContextValue | null>(null)

export function useRateLimit() {
  const context = useContext(RateLimitContext)
  if (!context) {
    throw new Error('useRateLimit must be used within RateLimitProvider')
  }
  return context
}

interface RateLimitProviderProps {
  children: ReactNode
}

export function RateLimitProvider({ children }: RateLimitProviderProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | undefined>()
  const [currentTier, setCurrentTier] = useState<UserTier>('free')

  const showUpgradeModal = useCallback((error?: RateLimitError) => {
    setRateLimitError(error)
    setIsModalOpen(true)
  }, [])

  const hideUpgradeModal = useCallback(() => {
    setIsModalOpen(false)
    setRateLimitError(undefined)
  }, [])

  // Listen for global rate limit events
  useEffect(() => {
    const handleRateLimitEvent = (event: Event) => {
      const customEvent = event as CustomEvent<RateLimitEventDetail>
      const { error } = customEvent.detail
      
      // Update current tier from error if available
      if (error.tier) {
        setCurrentTier(error.tier)
      }
      
      showUpgradeModal(error)
    }

    window.addEventListener(RATE_LIMIT_EVENT, handleRateLimitEvent)
    return () => window.removeEventListener(RATE_LIMIT_EVENT, handleRateLimitEvent)
  }, [showUpgradeModal])

  return (
    <RateLimitContext.Provider value={{ showUpgradeModal, hideUpgradeModal, currentTier, setCurrentTier }}>
      {children}
      <UpgradeModal
        isOpen={isModalOpen}
        onClose={hideUpgradeModal}
        rateLimitError={rateLimitError}
        currentTier={currentTier}
      />
    </RateLimitContext.Provider>
  )
}
